import type { RouterConfig } from "nuxt/schema";
import { START_LOCATION } from "vue-router";
import { useNuxtApp } from "#app/nuxt";
import { isChangingPage } from "#app/components/utils";
import { isProductDetailPath } from "@/utils/productRoute";

const instantScrollBehavior = "instant" as unknown as ScrollBehavior;

const getHashElementScrollMarginTop = (selector: string) => {
  try {
    const element = document.querySelector(selector);
    if (element) {
      return (
        (Number.parseFloat(getComputedStyle(element).scrollMarginTop) || 0) +
        (Number.parseFloat(
          getComputedStyle(document.documentElement).scrollPaddingTop,
        ) || 0)
      );
    }
  } catch {
    //
  }

  return 0;
};

const calculatePosition = (
  to: Parameters<NonNullable<RouterConfig["scrollBehavior"]>>[0],
  from: Parameters<NonNullable<RouterConfig["scrollBehavior"]>>[1],
  savedPosition: Parameters<NonNullable<RouterConfig["scrollBehavior"]>>[2],
  defaultHashScrollBehavior: ScrollBehavior,
) => {
  if (savedPosition) {
    return savedPosition;
  }

  const isPageNavigation = isChangingPage(to, from);

  if (to.hash) {
    return {
      el: to.hash,
      top: getHashElementScrollMarginTop(to.hash),
      behavior: isPageNavigation
        ? defaultHashScrollBehavior
        : instantScrollBehavior,
    };
  }

  if (isProductDetailPath(to.path)) {
    return {
      left: 0,
      top: 0,
      behavior: instantScrollBehavior,
    };
  }

  return {
    left: 0,
    top: 0,
  };
};

export default <RouterConfig>{
  scrollBehavior(to, from, savedPosition) {
    const nuxtApp = useNuxtApp();
    const hashScrollBehavior = "auto";

    if (to.path === from.path) {
      if (from.hash && !to.hash) {
        return { left: 0, top: 0 };
      }

      if (to.hash) {
        return {
          el: to.hash,
          top: getHashElementScrollMarginTop(to.hash),
          behavior: hashScrollBehavior,
        };
      }

      return false;
    }

    const routeAllowsScrollToTop =
      typeof to.meta.scrollToTop === "function"
        ? to.meta.scrollToTop(to, from)
        : to.meta.scrollToTop;

    if (routeAllowsScrollToTop === false) {
      return false;
    }

    const hookToWait = nuxtApp._runningTransition
      ? "page:transition:finish"
      : "page:loading:end";

    return new Promise((resolve) => {
      if (from === START_LOCATION) {
        resolve(calculatePosition(to, from, savedPosition, hashScrollBehavior));
        return;
      }

      nuxtApp.hooks.hookOnce(hookToWait, () => {
        requestAnimationFrame(() => {
          resolve(
            calculatePosition(to, from, savedPosition, hashScrollBehavior),
          );
        });
      });
    });
  },
};
